import React, { useEffect } from "react";
import { useForm } from "react-hook-form";
import { useSelector, useDispatch } from "react-redux";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { updateStep, clearValidation } from "../../../redux/profileFormSlice";
import ValidationError from "../../../components/ValidationError/ValidationError";

const schema = yup.object({
  interestReason: yup
    .array()
    .min(1, "Please select at least one reason for using Ontrack Careers"),
});

const options = [
  { field: "assessAIImpact", label: "To assess how AI might impact my job" },
  { field: "personalizedRecommendations", label: "To find personalized upskilling recommendations" },
  { field: "exploreCareerPaths", label: "To explore new career paths" },
  { field: "prepareForJobMarket", label: "To prepare for future job market shifts" },
  { field: "other", label: "Other" },
];

const InterestAndReason = () => {
  const dispatch = useDispatch();
  const initialOptions = useSelector((s) => s.profileForm.step2?.interestReason) || [];
  const validationStep = useSelector((s) => s.profileForm.validationStep);

  const {
    watch,
    setValue,
    trigger,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
    mode: "onChange", 
    defaultValues: { interestReason: initialOptions },
  });

  const selectedOptions = watch("interestReason");

  // Sync with Redux
  useEffect(() => {
    dispatch(updateStep({ step: "step2", data: { interestReason: selectedOptions } }));
  }, [selectedOptions, dispatch]);

  // Validate on Redux trigger
  useEffect(() => {
    if (validationStep === 2) {
      trigger("interestReason");
      dispatch(clearValidation());
    }
  }, [validationStep, trigger, dispatch]);

  const toggleOption = (option) => {
    const newOptions = selectedOptions.includes(option)
      ? selectedOptions.filter((item) => item !== option)
      : [...selectedOptions, option];

    setValue("interestReason", newOptions, { shouldValidate: true });
  };

  return (
    <div className="max-w-4xl mx-auto">
      <div className="text-center mb-4">
        <h2 className="text-base sm:text-lg font-semibold text-gray-800 mb-1 sm:mb-2">Interest & Reason</h2>
        <h3 className="text-lg sm:text-xl font-medium text-gray-800 mb-2">
          Why are you interested in using Ontrack Careers?
        </h3>
      </div>

      <ValidationError message={errors.interestReason?.message} />

      <div className="space-y-4">
        {options.map(({ field, label }) => (
          <label
            key={field}
            className="flex items-start sm:items-center gap-3 cursor-pointer border border-gray-200 rounded-lg p-4 sm:p-5 hover:bg-gray-50 transition-colors h-full"
          >
            <input
              type="checkbox"
              checked={selectedOptions.includes(field)}
              onChange={() => toggleOption(field)}
              className="w-4 h-4 text-purple-600 bg-gray-100 border-gray-300 rounded focus:ring-purple-500 focus:ring-2"
            />
            <span className="text-gray-800 font-medium">{label}</span>
          </label>
        ))}
      </div>
    </div>
  );
};

export default InterestAndReason;
